import { useState, useEffect, useCallback } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import { FiX, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usePdfConfig } from '../../context/PdfConfigContext';
import { upsertPdfConfig, clearPdfConfig, clearAllPdfConfigs } from '../../services/api';
import { PDF_REGISTRY, resolvePdfPreferences } from '../../pdf/lib/registry';
import { PDF_THEMES } from '../../pdf/lib/themes';
import { DOCUMENT_MAP } from '../../pdf/usePdfDownload';
import { SAMPLE_DATA } from './pdfConfigSampleData';
import styles from './PdfConfigPage.module.css';

const PDF_THEME_OPTIONS = [
  { id: 'saffron',  label: 'Saffron',  color: PDF_THEMES.saffron.primary  },
  { id: 'navy',     label: 'Navy',     color: PDF_THEMES.navy.primary      },
  { id: 'emerald',  label: 'Emerald',  color: PDF_THEMES.emerald.primary   },
  { id: 'charcoal', label: 'Charcoal', color: PDF_THEMES.charcoal.primary  },
  { id: 'maroon',   label: 'Maroon',   color: PDF_THEMES.maroon.primary    },
];

const TABS = [
  { id: 'accounting',   label: 'Accounting Reports' },
  { id: 'memberReports',label: 'Member Reports'     },
  { id: 'transactions', label: 'Transactions'       },
];

// Preview-only types (modal shows live preview + options); non-preview auto-download
const PREVIEW_TYPES = Object.entries(PDF_REGISTRY)
  .filter(([, v]) => v.preview)
  .map(([k]) => k);

const buildLocalState = (savedConfigs) => {
  const state = {};
  Object.keys(PDF_REGISTRY).forEach((type) => {
    const saved = savedConfigs[type] || null;
    const resolved = resolvePdfPreferences(saved, type);
    state[type] = { ...resolved, dirty: false };
  });
  return state;
};

const PdfConfigPage = () => {
  const { configs, reload } = usePdfConfig();
  const [local, setLocal]     = useState(() => buildLocalState({}));
  const [activeTab, setTab]   = useState('accounting');
  const [saving, setSaving]   = useState(null);   // reportType being saved
  const [savingAll, setSavingAll] = useState(false);
  const [preview, setPreview] = useState(null);   // { type, theme, orientation, sections }

  useEffect(() => {
    // Rebuild from the latest saved configs but preserve any unsaved (dirty) changes.
    setLocal((prev) => {
      const fresh = buildLocalState(configs);
      Object.keys(prev).forEach((type) => {
        if (prev[type]?.dirty) fresh[type] = prev[type];
      });
      return fresh;
    });
  }, [configs]);

  const patch = useCallback((type, key, val) => {
    setLocal((prev) => ({
      ...prev,
      [type]: { ...prev[type], [key]: val, dirty: true },
    }));
  }, []);

  const patchSection = useCallback((type, id, val) => {
    setLocal((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        sections: { ...prev[type].sections, [id]: val },
        dirty: true,
      },
    }));
  }, []);

  const resetType = useCallback((type) => {
    const fresh = resolvePdfPreferences(null, type);
    setLocal((prev) => ({ ...prev, [type]: { ...fresh, dirty: true } }));
  }, []);

  const saveOne = async (type) => {
    setSaving(type);
    try {
      const { theme, orientation, sections } = local[type];
      await upsertPdfConfig(type, { theme, orientation, sections });
      // Mark clean before reload so the useEffect preserves other dirty types correctly
      setLocal((prev) => ({ ...prev, [type]: { ...prev[type], dirty: false } }));
      await reload();
      toast.success(`${PDF_REGISTRY[type].label} defaults saved`);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const saveAll = async () => {
    const dirtyTypes = Object.keys(local).filter((t) => local[t].dirty);
    if (!dirtyTypes.length) { toast.info('Nothing changed'); return; }
    setSavingAll(true);
    try {
      await Promise.all(
        dirtyTypes.map((type) => {
          const { theme, orientation, sections } = local[type];
          return upsertPdfConfig(type, { theme, orientation, sections });
        })
      );
      await reload();
      setLocal((prev) => {
        const next = { ...prev };
        dirtyTypes.forEach((t) => { next[t] = { ...next[t], dirty: false }; });
        return next;
      });
      toast.success(`Saved ${dirtyTypes.length} report configuration${dirtyTypes.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to save all');
    } finally {
      setSavingAll(false);
    }
  };

  const clearOne = async (type) => {
    try {
      await clearPdfConfig(type);
      await reload();
      toast.success(`${PDF_REGISTRY[type].label} reset to defaults`);
    } catch {
      toast.error('Failed to clear');
    }
  };

  const clearAll = async () => {
    try {
      await clearAllPdfConfigs();
      await reload();
      toast.success('All PDF defaults cleared — modal will appear each time');
    } catch {
      toast.error('Failed to clear all');
    }
  };

  const openPreview = (type) => {
    const { theme, orientation, sections } = local[type];
    setPreview({ type, theme, orientation, sections });
  };

  const typesForTab = Object.entries(PDF_REGISTRY)
    .filter(([, v]) => v.tab === activeTab)
    .map(([k]) => k);

  const anyDirty = Object.values(local).some((v) => v.dirty);
  const hasSavedAny = Object.keys(configs).length > 0;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h2>PDF Configuration</h2>
      </div>

      <div className={styles.statusBanner}>
        {hasSavedAny ? (
          <span className={styles.statusActive}>
            ✓ Defaults saved — clicking PDF on reports downloads immediately
          </span>
        ) : (
          <span className={styles.statusDefault}>
            No defaults saved yet — the preview modal appears on every export
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabBar}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Report cards */}
      <div className={styles.reportList}>
        {typesForTab.map((type) => {
          const reg       = PDF_REGISTRY[type];
          const state     = local[type] || {};
          const isSaved   = !!configs[type];
          const isPreviewable = PREVIEW_TYPES.includes(type) && DOCUMENT_MAP[type] && SAMPLE_DATA[type];

          return (
            <div key={type} className={`${styles.reportCard} ${state.dirty ? styles.reportCardDirty : ''}`}>
              <div className={styles.reportCardHeader}>
                <div className={styles.reportCardTitle}>
                  <h3>{reg.label}</h3>
                  {isSaved && <span className={styles.savedBadge}>Saved</span>}
                  {state.dirty && <span className={styles.dirtyBadge}>Unsaved</span>}
                </div>
                <div className={styles.reportCardActions}>
                  {isPreviewable && (
                    <button className="btn btn-ghost btn-sm" onClick={() => openPreview(type)} title="Preview PDF">
                      <FiEye size={14} /> Preview
                    </button>
                  )}
                  {isSaved && (
                    <button className="btn btn-ghost btn-sm" onClick={() => clearOne(type)}>
                      Reset
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => saveOne(type)}
                    disabled={saving === type}
                  >
                    {saving === type ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Theme */}
              <div className={styles.row}>
                <span className={styles.rowLabel}>Theme</span>
                <div className={styles.themeRow}>
                  {PDF_THEME_OPTIONS.map((t) => (
                    <button
                      key={t.id}
                      className={`${styles.themeChip} ${state.theme === t.id ? styles.themeChipActive : ''}`}
                      onClick={() => patch(type, 'theme', t.id)}
                      title={t.label}
                    >
                      <span className={styles.themeChipSwatch} style={{ background: t.color }} />
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Orientation */}
              <div className={styles.row}>
                <span className={styles.rowLabel}>Orientation</span>
                <div className={styles.orientRow}>
                  {['portrait', 'landscape'].map((o) => (
                    <button
                      key={o}
                      className={`${styles.orientBtn} ${state.orientation === o ? styles.orientBtnActive : ''}`}
                      onClick={() => patch(type, 'orientation', o)}
                    >
                      {o.charAt(0).toUpperCase() + o.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sections */}
              <div className={styles.row}>
                <span className={styles.rowLabel}>Include</span>
                <div className={styles.checkList}>
                  {reg.sections.map(({ id, label }) => (
                    <label key={id} className={styles.checkRow}>
                      <input
                        type="checkbox"
                        checked={!!(state.sections && state.sections[id])}
                        onChange={(e) => patchSection(type, id, e.target.checked)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom actions */}
      <div className={styles.actions}>
        {anyDirty && (
          <button className="btn btn-primary" onClick={saveAll} disabled={savingAll}>
            {savingAll ? 'Saving…' : 'Save All Changes'}
          </button>
        )}
        {hasSavedAny && (
          <button className="btn btn-ghost" onClick={clearAll}>
            Clear All Defaults
          </button>
        )}
      </div>

      {/* Preview Modal */}
      {preview && (
        <PreviewModal
          type={preview.type}
          theme={preview.theme}
          orientation={preview.orientation}
          sections={preview.sections}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

function PreviewModal({ type, theme, orientation, sections, onClose }) {
  const DocumentComponent = DOCUMENT_MAP[type];
  const sampleData        = SAMPLE_DATA[type];
  if (!DocumentComponent || !sampleData) return null;

  return (
    <div className={styles.previewOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.previewModal}>
        <div className={styles.previewHeader}>
          <span>{PDF_REGISTRY[type].label} — Preview ({orientation})</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX /></button>
        </div>
        <div className={styles.previewBody}>
          <PDFViewer width="100%" height="100%" showToolbar={false}>
            <DocumentComponent data={sampleData} theme={theme} orientation={orientation} sections={sections} />
          </PDFViewer>
        </div>
      </div>
    </div>
  );
}

export default PdfConfigPage;
